import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	insertFurnitureIntoCache,
	reorderFurnitureInCache,
	spliceErefIntoCache,
} from "helpers/quotationCache.helper"
import { useCreateFurniture, useLinkFurnitureCustomModelNumber } from "hooks/api/quotation/quotations.hook"
import AnnotationService from "services/annotations.service"
import type {
	TCreateFurnitureResponse,
	TFurniture,
	TFurnitureCustomization,
	TFurnitureOrdering,
	TUpdateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import { useAreaCreate, type TAreaCreateSession } from "./useAreaCreate"
import { useApplyCustomization } from "./useApplyCustomization"

/** Live "add custom object" session — the shared area-create session under its own name. */
export type TCustomCreateSession = TAreaCreateSession

/** What the create panel has collected by the time Create object is pressed. */
export type TCustomCreateDetails = {
	width: number
	height: number
	depth: number
	/** The picked custom model number's id — mandatory, the panel keeps Create disabled without it. */
	customModelNumberId: number
	/** The elevation reference to link to, at the version the cache last saw — mandatory (#1371). */
	eref: { id: number; version: number }
	/**
	 * REQ-413/414. Null/absent unless the panel's Customization section is expanded — the
	 * customization endpoint is per-object, so it becomes a third link in the chain below.
	 */
	customization?: TFurnitureCustomization | null
}

type Props = {
	/** Orchestrator-owned mirror of the live session (see useAreaCreate). */
	createSessionRef: MutableRefObject<TCustomCreateSession | null>
	/** Plain (non-React) draft box + its four corner handles, from createOverlayContainers. */
	draftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	/** `.App` element — the origin the box's app-relative coordinates are measured against. */
	appContainerRef: MutableRefObject<HTMLElement | null>
	/** The live viewer panes, re-resolved on every call (they are replaced on a layout change). */
	getViewerPanes: () => TPaneBinding[]
	/** Bumped by split-screen enter/exit; re-binds the canvas listeners onto the new panes. */
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	queryClient: QueryClient
	/** Post-create hand-off to the orchestrator: select the new object and open its sidebar. */
	onFurnitureCreated: (furniture: TFurniture) => void
	/** The shared tool command (useCanvasTools) — the mode puts every pane back into select. */
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

/**
 * "Add custom object" mode (#1371), opened from the header's + Add → Custom object menu. Same
 * shared `useAreaCreate` gesture as the other create flows; this hook owns the create chain a
 * custom object needs.
 *
 * A custom object has no Stevens model number and no real category, and the create endpoint has
 * no `"custom"` shortcut yet (backend #2111, not shipped) — so creation goes through the same
 * `related_category_id: null` door as "Other" first, then
 * `PATCH /quotations/{id}/furniture/{id}/custom-model-number/` both links the picked custom model
 * number AND converts the object to a custom one, in the same request (see the service method's
 * docstring). One PATCH covers what "Other" needs two for, because a custom object's name/price
 * live on the linked definition rather than on a config this flow has to fill in itself.
 *
 * Unlike "Other"'s two PATCHes, this one is not optional best-effort in intent — the panel already
 * keeps Create disabled until a model number is picked — but a failure is still reported and the
 * object handed off regardless, so a user is never left facing a vanished object over a network
 * blip: the object already exists after step 1 no matter how step 2 goes.
 *
 * BOTH steps carry their own `ordering`, and both get applied — not just step 1's. Per the
 * endpoint's own contract, converting to a custom object clears the category and can move the
 * object within its render group all over again, so step 1's ordering (correct for the
 * pre-conversion "Other" shape) can go stale the moment step 2 lands; applying step 2's afterward
 * is what keeps the final render order matching the object's actual, final shape.
 *
 * A third, optional step (REQ-413/414) chains the Customization section's own
 * `PUT …/customization/` on top, against the version step 2 last returned — only when the panel's
 * section actually holds one, same contract `useApplyCustomization` already applies to the
 * cabinet/Other/countertop flows. Best-effort like step 2: past step 1 the object exists, so a
 * rejection here is surfaced and the hand-off still happens rather than leaving an object nothing
 * points at.
 */
export const useCustomObjectCreate = ({
	createSessionRef,
	draftRectRef,
	appContainerRef,
	getViewerPanes,
	layoutEpoch,
	numericQuotationIdRef,
	queryClient,
	onFurnitureCreated,
	onToolChange,
	clearSelectionToolbar,
	success,
	formatError,
}: Props) => {
	const { mutateAsync: createFurnitureAsync } = useCreateFurniture()
	const { mutateAsync: linkCustomModelNumberAsync } = useLinkFurnitureCustomModelNumber()
	const applyCustomization = useApplyCustomization(formatError)
	const [isCreatePending, setIsCreatePending] = useState(false)

	const areaCreate = useAreaCreate({
		createSessionRef,
		draftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})
	const { cancelCreateArea } = areaCreate

	// Step 2 of the chain. Returns the object as the backend last described it, PLUS its own
	// `ordering` — converting to a custom object clears the category and can move the object within
	// its render group, same as the create response's own `ordering` does for the pre-conversion
	// "Other" shape, so this one has to be applied too, not just extracted-and-dropped. Throws
	// nothing: a rejected PATCH is reported and the best-known (still "Other"-shaped) object is
	// returned, with no ordering of its own to apply.
	const applyCustomModelNumber = useCallback(
		async (
			quotationId: number,
			created: TFurniture,
			customModelNumberId: number,
		): Promise<{ furniture: TFurniture; ordering: TFurnitureOrdering | null }> => {
			try {
				const response = await linkCustomModelNumberAsync({
					quotationId,
					furnitureId: created.id,
					body: { version: created.version, custom_model_number_id: customModelNumberId },
				})
				const data = response?.data as TUpdateFurnitureResponse | undefined
				return { furniture: data?.this_furniture ?? created, ordering: data?.ordering ?? null }
			} catch (error) {
				formatError(error)
				return { furniture: created, ordering: null }
			}
		},
		[linkCustomModelNumberAsync, formatError],
	)

	const runCreateCustom = useCallback(
		async (details: TCustomCreateDetails) => {
			const quotationId = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (quotationId == null || Number.isNaN(quotationId) || !area) return
			setIsCreatePending(true)
			try {
				const response = await createFurnitureAsync({
					quotationId,
					body: {
						page_number: area.pageNumber,
						x0: area.coordinates.x0,
						y0: area.coordinates.y0,
						x1: area.coordinates.x1,
						y1: area.coordinates.y1,
						// Null for the same reason "Other" sends it: no `"custom"` create shortcut yet.
						related_category_id: null,
						width: details.width,
						height: details.height,
						depth: details.depth,
						eref_id: details.eref.id,
						eref_version: details.eref.version,
					},
				})
				const data = response?.data as TCreateFurnitureResponse | undefined
				const createdFurniture = data?.this_furniture
				if (!createdFurniture) return

				// Step 2. Past this line the object exists no matter how it went, so every path below
				// tears the mode down and hands over.
				const { furniture: linked, ordering: modelNumberOrdering } = await applyCustomModelNumber(
					quotationId,
					createdFurniture,
					details.customModelNumberId,
				)

				// Step 3 (REQ-413/414) — only when the panel's section actually holds one.
				const created = await applyCustomization(quotationId, linked, details.customization ?? null)

				let isInCache = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					let next = insertFurnitureIntoCache(cached, created)
					if (next !== cached) isInCache = true
					// Step 1's ordering positions the freshly-inserted object within its group; step 2's
					// (if the conversion changed it) is applied on top, so the FINAL custom-object shape
					// is what the group ends up sorted by.
					if (data?.ordering) next = reorderFurnitureInCache(next, data.ordering)
					if (modelNumberOrdering) next = reorderFurnitureInCache(next, modelNumberOrdering)
					return data?.updated_eref ? spliceErefIntoCache(next, data.updated_eref) : next
				})

				if (!isInCache) queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })

				cancelCreateArea()

				if (isInCache) AnnotationService.addFurnitureAnnotation(created)
				onFurnitureCreated(created)
				if (isInCache) AnnotationService.selectFurniture(created.id)
				success("Object created")
			} catch (error) {
				formatError(error)
			} finally {
				setIsCreatePending(false)
			}
		},
		[
			numericQuotationIdRef,
			createSessionRef,
			createFurnitureAsync,
			applyCustomModelNumber,
			applyCustomization,
			queryClient,
			cancelCreateArea,
			onFurnitureCreated,
			success,
			formatError,
		],
	)

	return {
		createSession: areaCreate.createSession,
		isCreatePending,
		startCreateCustom: areaCreate.startCreateArea,
		cancelCreateCustom: areaCreate.cancelCreateArea,
		runCreateCustom,
	}
}
